import { useMemo, useState } from "react";
import PriceHistoryChart from "../../components/PriceHistoryChart";
import {
  cardStyle,
  ghostButtonStyle,
  inputStyle,
  primaryButtonStyle,
  SectionTitle,
} from "../dashboardStyles";
import {
  formatCapturedAt,
  formatCoordinate,
  formatCurrency,
  getPermissionBadgeStyle,
} from "../dashboardUtils";

function buildGoogleMapsUrl(market) {
  if (!market) {
    return "";
  }

  const query =
    String(market.address || "").trim() ||
    (Number.isFinite(Number(market.latitude)) && Number.isFinite(Number(market.longitude))
      ? `${market.latitude},${market.longitude}`
      : "");

  if (!query) {
    return "";
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function InsightCard({ label, value, helper, tone = "prompt" }) {
  return (
    <div
      style={{
        padding: 16,
        borderRadius: 20,
        border: "1px solid #e5e7eb",
        background: "#ffffff",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#475569" }}>{label}</div>
        <span style={getPermissionBadgeStyle(tone)}>{value}</span>
      </div>
      {helper ? <div style={{ marginTop: 10, color: "#64748b" }}>{helper}</div> : null}
    </div>
  );
}

function formatFreshness(entry, t) {
  if (entry?.freshness_text && !t) {
    return entry.freshness_text;
  }
  const days = Number(entry?.price_age_days);
  const fallback =
    entry?.freshness_text ||
    (Number.isFinite(days) ? `Updated ${days} days ago` : "Unknown update date");
  return (
    t?.(`priceFreshness.${entry?.freshness_label || "unknown"}`, fallback, {
      days,
    }) || fallback
  );
}

function buildPriceHistoryMetrics(entry, t) {
  const recentHistory = [...(entry?.recent_history || [])].reverse();
  const oldestPoint = recentHistory[0] || null;
  const latestPoint = recentHistory[recentHistory.length - 1] || null;
  const prices = recentHistory
    .map((item) => Number(item.price))
    .filter((value) => Number.isFinite(value));
  const averagePrice =
    prices.length > 0 ? prices.reduce((sum, value) => sum + value, 0) / prices.length : null;
  const priceDelta =
    oldestPoint && latestPoint ? Number(latestPoint.price) - Number(oldestPoint.price) : null;
  const inflationPercentage =
    oldestPoint && latestPoint && Number(oldestPoint.price) > 0
      ? (priceDelta / Number(oldestPoint.price)) * 100
      : null;

  return {
    recentHistory,
    averagePrice,
    inflationPercentage,
    inflationLabel:
      priceDelta === null
        ? t?.("market.history.insufficient", "Sem historico suficiente") || "Sem historico suficiente"
        : priceDelta > 0
          ? t?.("market.history.up", "Alta de {{amount}}", {
              amount: formatCurrency(priceDelta),
            }) || `Alta de ${formatCurrency(priceDelta)}`
          : priceDelta < 0
            ? t?.("market.history.down", "Queda de {{amount}}", {
                amount: formatCurrency(Math.abs(priceDelta)),
              }) || `Queda de ${formatCurrency(Math.abs(priceDelta))}`
            : t?.("market.history.stable", "Preco estavel") || "Preco estavel",
    inflationTone:
      priceDelta === null
        ? "unsupported"
        : priceDelta > 0
          ? "denied"
          : priceDelta < 0
            ? "granted"
            : "prompt",
  };
}

export default function MarketPage({
  goToMap,
  isLoadingMarketPrices,
  marketCatalogSyncMeta,
  selectedMarket,
  selectedMarketFavorite,
  selectedMarketInsight,
  selectedMarketProduct,
  selectedMarketPrices,
  selectMarketProduct,
  t,
  toggleFavorite,
}) {
  const [catalogQuery, setCatalogQuery] = useState("");
  const normalizedCatalogQuery = catalogQuery.trim().toLowerCase();
  const filteredMarketPrices = useMemo(() => {
    if (!normalizedCatalogQuery) {
      return selectedMarketPrices;
    }

    return selectedMarketPrices.filter((entry) => {
      const haystack = [
        entry.product,
        entry.brand,
        entry.category,
        entry.offer_description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedCatalogQuery);
    });
  }, [normalizedCatalogQuery, selectedMarketPrices]);

  const visibleMarketPrices = useMemo(() => {
    if (normalizedCatalogQuery) {
      return filteredMarketPrices.slice(0, 60);
    }
    return filteredMarketPrices.slice(0, 12);
  }, [filteredMarketPrices, normalizedCatalogQuery]);

  const selectedMarketMapsUrl = useMemo(
    () => buildGoogleMapsUrl(selectedMarket),
    [selectedMarket]
  );

  const openMarketInGoogleMaps = () => {
    if (!selectedMarketMapsUrl || typeof window === "undefined") {
      return;
    }
    window.location.assign(selectedMarketMapsUrl);
  };

  if (!selectedMarket) {
    return (
      <div style={cardStyle}>
        <SectionTitle
          eyebrow={t?.("market.empty.eyebrow", "Mercado") || "Mercado"}
          title={t?.("market.empty.title", "Escolha um mercado no mapa ou na busca") || "Escolha um mercado no mapa ou na busca"}
          description={t?.("market.empty.description", "Assim que um mercado for selecionado, esta pagina abre o catalogo mais atualizado dele com promocoes, historico e rota.") || "Assim que um mercado for selecionado, esta pagina abre o catalogo mais atualizado dele com promocoes, historico e rota."}
          action={
            <button onClick={goToMap} style={primaryButtonStyle}>
              {t?.("market.empty.open_map", "Abrir mapa") || "Abrir mapa"}
            </button>
          }
        />
      </div>
    );
  }

  const selectedProductMetrics = buildPriceHistoryMetrics(selectedMarketProduct, t);
  const selectedProductHistory = selectedProductMetrics.recentHistory;
  const featuredPromotions = selectedMarketPrices
    .filter((entry) => entry.offer_description || entry.price_mode === "promotion" || entry.price_mode === "club")
    .slice(0, 4);
  const catalogSyncMeta = marketCatalogSyncMeta || {
    status: "idle",
    provider: "",
    imported: 0,
    skipped: 0,
    message: "",
  };
  const syncToneMap = {
    synced: "granted",
    no_change: "prompt",
    no_provider: "unsupported",
    no_data: "unsupported",
    error: "denied",
  };
  const syncTone = syncToneMap[catalogSyncMeta.status] || "prompt";
  const syncTitleMap = {
    synced:
      t?.("market.sync.synced", "Catalogo sincronizado automaticamente") ||
      "Catalogo sincronizado automaticamente",
    no_change:
      t?.("market.sync.no_change", "Feed automatico consultado") ||
      "Feed automatico consultado",
    no_provider:
      t?.("market.sync.no_provider", "Sem feed automatico para esta rede") ||
      "Sem feed automatico para esta rede",
    no_data:
      t?.("market.sync.no_data", "Feed automatico sem itens") ||
      "Feed automatico sem itens",
    error:
      t?.("market.sync.error", "Falha na sincronizacao automatica") ||
      "Falha na sincronizacao automatica",
  };
  const syncMessageMap = {
    synced:
      catalogSyncMeta.imported > 0
        ? t?.(
            "market.sync.synced_with_count",
            "{{count}} preco(s) novo(s) entraram no catalogo deste mercado automaticamente.",
            { count: catalogSyncMeta.imported }
          ) ||
          `${catalogSyncMeta.imported} preco(s) novo(s) entraram no catalogo deste mercado automaticamente.`
        : t?.(
            "market.sync.synced_without_count",
            "O catalogo automatico foi consultado para este mercado."
          ) || "O catalogo automatico foi consultado para este mercado.",
    no_change:
      t?.(
        "market.sync.no_change_message",
        "A fonte automatica foi consultada e nao trouxe novidades agora."
      ) || "A fonte automatica foi consultada e nao trouxe novidades agora.",
    no_provider:
      t?.(
        "market.sync.no_provider_message",
        "Ainda nao existe uma fonte automatica configurada para esse mercado. O app continua mostrando o catalogo disponivel mais recente."
      ) ||
      "Ainda nao existe uma fonte automatica configurada para esse mercado. O app continua mostrando o catalogo disponivel mais recente.",
    no_data:
      t?.(
        "market.sync.no_data_message",
        "Existe uma fonte configurada, mas ela nao devolveu produtos agora."
      ) || "Existe uma fonte configurada, mas ela nao devolveu produtos agora.",
    error:
      catalogSyncMeta.message ||
      (t?.(
        "market.sync.error_message",
        "Nao foi possivel sincronizar o catalogo automatico agora."
      ) || "Nao foi possivel sincronizar o catalogo automatico agora."),
  };

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div style={{ ...cardStyle, padding: 24 }}>
        <SectionTitle
          eyebrow={t?.("market.hero.eyebrow", "Pagina do mercado") || "Pagina do mercado"}
          title={selectedMarket.market}
          description={selectedMarket.banner_note || selectedMarket.address || (t?.("map.ready.address_missing", "Endereco ainda nao informado") || "Endereco ainda nao informado")}
          action={
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                onClick={() =>
                  toggleFavorite({
                    entity_type: "market",
                    entity_id: selectedMarket.market_id,
                    title: selectedMarket.market,
                    subtitle: selectedMarket.address || "",
                  })
                }
                style={ghostButtonStyle}
              >
                {selectedMarketFavorite
                  ? t?.("common.remove_favorite", "Remover favorito") || "Remover favorito"
                  : t?.("common.favorite", "Favoritar") || "Favoritar"}
              </button>
              {selectedMarketMapsUrl ? (
                <button onClick={openMarketInGoogleMaps} style={ghostButtonStyle}>
                  {t?.("list.market_detail.maps", "Abrir no Google Maps") ||
                    "Abrir no Google Maps"}
                </button>
              ) : null}
              <button onClick={goToMap} style={ghostButtonStyle}>
                {t?.("market.hero.back_map", "Voltar ao mapa") || "Voltar ao mapa"}
              </button>
            </div>
          }
        />
        <div
          style={{
            padding: 18,
            borderRadius: 22,
            background: "linear-gradient(135deg, #fff1f2 0%, #fff7ed 55%, #ffffff 100%)",
            border: "1px solid #fecdd3",
            marginBottom: 16,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 800, color: "#e11d48", textTransform: "uppercase" }}>
            {selectedMarket.banner_title || (t?.("market.hero.featured", "Mercado em destaque") || "Mercado em destaque")}
          </div>
          <div style={{ marginTop: 10, color: "#475569" }}>
            {selectedMarket.banner_note || (t?.("market.hero.featured_fallback", "Catalogo automatico com foco em preco atualizado, confiabilidade e promocoes.") || "Catalogo automatico com foco em preco atualizado, confiabilidade e promocoes.")}
          </div>
          <div style={{ marginTop: 10, color: "#9a3412" }}>
            {(t?.("market.hero.category", "Categoria") || "Categoria") + ": "}
            {selectedMarket.category || (t?.("common.market_lower", "mercado") || "mercado")}
            {selectedMarket.neighborhood ? ` | ${selectedMarket.neighborhood}` : ""}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
          }}
        >
          <InsightCard
            label={t?.("common.distance", "Distancia") || "Distancia"}
            value={selectedMarketInsight?.distanceLabel || (t?.("common.no_data", "n/d") || "n/d")}
            helper={t?.("market.info.distance_helper", "Calculada a partir da localizacao ativa do dispositivo.") || "Calculada a partir da localizacao ativa do dispositivo."}
          />
          <InsightCard
            label={t?.("market.info.rating", "Avaliacao PennyPantry") || "Avaliacao PennyPantry"}
            value={selectedMarketInsight?.ratingLabel || (t?.("common.no_data", "n/d") || "n/d")}
            helper={
              selectedMarketInsight?.priceIndexLabel
                ? t?.("market.info.index_helper", "Indice {{value}}", {
                    value: selectedMarketInsight.priceIndexLabel,
                  }) || `Indice ${selectedMarketInsight.priceIndexLabel}`
                : t?.(
                    "market.info.rating_growth",
                    "Vai melhorando conforme o mercado recebe mais dados."
                  ) || "Vai melhorando conforme o mercado recebe mais dados."
            }
            tone="granted"
          />
          <InsightCard
            label={t?.("common.reliability", "Confiabilidade") || "Confiabilidade"}
            value={
              t?.(
                "market.info.reliability_value",
                selectedMarketInsight?.reliabilityLabel || "Inicial"
              ) ||
              selectedMarketInsight?.reliabilityLabel ||
              "Inicial"
            }
            helper={
              t?.("market.info.reliability_helper", "{{products}} produtos ativos | {{contributions}} atualizacoes", {
                products: selectedMarketInsight?.productsCount || 0,
                contributions: selectedMarketInsight?.contributions || 0,
              }) ||
              `${selectedMarketInsight?.productsCount || 0} produtos ativos | ${selectedMarketInsight?.contributions || 0} atualizacoes`
            }
            tone={selectedMarketInsight?.reliabilityTone || "prompt"}
          />
          <InsightCard
            label={t?.("market.info.coordinates", "Coordenadas") || "Coordenadas"}
            value={`${formatCoordinate(selectedMarket.latitude)} / ${formatCoordinate(selectedMarket.longitude)}`}
            helper={t?.("market.info.coordinates_helper", "Usadas para mostrar o mercado corretamente no mapa.") || "Usadas para mostrar o mercado corretamente no mapa."}
          />
        </div>
      </div>

      {featuredPromotions.length > 0 ? (
        <div style={cardStyle}>
          <SectionTitle
            eyebrow={t?.("market.promotions.eyebrow", "Promocoes") || "Promocoes"}
            title={t?.("market.promotions.title", "Ofertas em destaque deste mercado") || "Ofertas em destaque deste mercado"}
            description={t?.("market.promotions.description", "Esses itens estao com promocao declarada, preco clube ou condicao especial nas leituras mais recentes.") || "Esses itens estao com promocao declarada, preco clube ou condicao especial nas leituras mais recentes."}
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12,
            }}
          >
            {featuredPromotions.map((entry) => (
              <div
                key={`promo-${entry.price_id}`}
                style={{
                  padding: 16,
                  borderRadius: 20,
                  border: "1px solid #fed7aa",
                  background: "#fff7ed",
                }}
              >
                <strong>{entry.product}</strong>
                <div style={{ marginTop: 8, color: "#ef4444", fontWeight: 800 }}>
                  {formatCurrency(entry.price)}
                </div>
                <div style={{ marginTop: 8, color: "#7c3aed" }}>
                  {entry.promotion_title ||
                    entry.offer_description ||
                    (t?.("market.promotions.community", "Promocao em destaque") ||
                      "Promocao em destaque")}
                </div>
                <div style={{ marginTop: 8, color: "#475569" }}>
                  {t?.("market.promotions.mode", "modo: {{mode}}", {
                    mode:
                      entry.price_mode === "standard"
                        ? t?.("common.mode.standard", "Normal") || "Normal"
                        : entry.price_mode === "promotion"
                          ? t?.("common.mode.promotion", "Promocao") || "Promocao"
                          : t?.("common.mode.club", "Clube") || "Clube",
                  }) || ""}
                  {entry.requires_membership
                    ? ` | ${t?.("market.promotions.requires_membership", "exige clube") || "exige clube"}`
                    : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div
        style={{ display: "grid", gridTemplateColumns: "1.02fr 0.98fr", gap: 20 }}
        className="stack-on-mobile"
      >
        <div style={cardStyle}>
          <SectionTitle
            eyebrow={t?.("market.catalog.eyebrow", "Catalogo") || "Catalogo"}
            title={t?.("market.catalog.title", "Produtos mais recentes deste mercado") || "Produtos mais recentes deste mercado"}
            description={t?.("market.catalog.description", "O catalogo mostra apenas o registro mais novo de cada produto. Os antigos continuam guardados no banco para historico e comparacao.") || "O catalogo mostra apenas o registro mais novo de cada produto. Os antigos continuam guardados no banco para historico e comparacao."}
            action={
              isLoadingMarketPrices ? (
                <span style={getPermissionBadgeStyle("prompt")}>
                  {t?.("market.catalog.updating", "Atualizando catalogo...") || "Atualizando catalogo..."}
                </span>
              ) : null
            }
          />
          <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
            <input
              style={inputStyle}
              placeholder={
                t?.("market.catalog.search_placeholder", "Buscar produto neste mercado") ||
                "Buscar produto neste mercado"
              }
              value={catalogQuery}
              onChange={(event) => setCatalogQuery(event.target.value)}
            />
            <div style={{ color: "#64748b", fontSize: 13 }}>
              {(t?.(
                "market.catalog.search_help",
                "Use a busca para reduzir a lista ou toque em um item para abrir o detalhe."
              ) || "Use a busca para reduzir a lista ou toque em um item para abrir o detalhe.")}
            </div>
            <div style={{ color: "#475569", fontSize: 13, fontWeight: 600 }}>
              {t?.("market.catalog.count", "Mostrando {{visible}} de {{total}} produtos", {
                visible: visibleMarketPrices.length,
                total: filteredMarketPrices.length,
              }) ||
                `Mostrando ${visibleMarketPrices.length} de ${filteredMarketPrices.length} produtos`}
            </div>
          </div>
          {!["idle", "disabled"].includes(catalogSyncMeta.status) ? (
            <div
              style={{
                padding: 14,
                borderRadius: 18,
                border: "1px solid #e5e7eb",
                background: catalogSyncMeta.status === "synced" ? "#ecfdf5" : "#f8fafc",
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <strong>
                  {syncTitleMap[catalogSyncMeta.status] ||
                    (t?.("market.sync.title_default", "Catalogo automatico") ||
                      "Catalogo automatico")}
                </strong>
                <span style={getPermissionBadgeStyle(syncTone)}>
                  {catalogSyncMeta.provider
                    ? t?.("market.sync.provider", "fonte {{provider}}", {
                        provider: catalogSyncMeta.provider,
                      }) || `fonte ${catalogSyncMeta.provider}`
                    : catalogSyncMeta.status}
                </span>
              </div>
              <div style={{ marginTop: 8, color: "#475569" }}>
                {syncMessageMap[catalogSyncMeta.status] || catalogSyncMeta.message}
              </div>
            </div>
          ) : null}

          {selectedMarketPrices.length > 0 ? (
            <div style={{ display: "grid", gap: 12 }}>
              {visibleMarketPrices.map((entry) => {
                const isSelected = selectedMarketProduct?.price_id === entry.price_id;
                const entryMetrics = buildPriceHistoryMetrics(entry, t);
                return (
                  <div
                    key={entry.price_id}
                    onClick={() => selectMarketProduct(entry.product)}
                    style={{
                      padding: 16,
                      borderRadius: 20,
                      border: isSelected ? "2px solid #ef4444" : "1px solid #e5e7eb",
                      background: isSelected ? "#fff1f2" : "#ffffff",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <strong>{entry.product}</strong>
                        <div style={{ marginTop: 6, color: "#ef4444", fontWeight: 800 }}>
                          {formatCurrency(entry.price)}
                        </div>
                        <div style={{ marginTop: 6, color: "#475569" }}>
                          {formatFreshness(entry, t)}
                        </div>
                        <div style={{ marginTop: 6, color: "#64748b" }}>
                          {t?.("market.catalog.source_meta", "Fonte: {{source}} | atualizacoes: {{count}}", {
                            source: entry.source_type,
                            count: entry.contributions,
                          }) || `Fonte: ${entry.source_type} | atualizacoes: ${entry.contributions}`}
                        </div>
                        {entry.trust_summary ? (
                          <div style={{ marginTop: 6, color: "#64748b" }}>
                            {t?.("market.catalog.trust_summary", "{{summary}}", {
                              summary: entry.trust_summary,
                            }) || entry.trust_summary}
                          </div>
                        ) : null}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {isSelected ? (
                          <span style={getPermissionBadgeStyle("granted")}>
                            {t?.("market.catalog.selected", "Selecionado") || "Selecionado"}
                          </span>
                        ) : null}
                        {entry.price_mode !== "standard" ? (
                          <span style={getPermissionBadgeStyle("prompt")}>{entry.price_mode}</span>
                        ) : null}
                      </div>
                    </div>
                    {entry.offer_description ? (
                      <div style={{ marginTop: 10, color: "#7c3aed" }}>
                        {t?.("market.catalog.current_promotion", "Promocao atual: {{offer}}", {
                          offer: entry.offer_description,
                        }) || `Promocao atual: ${entry.offer_description}`}
                      </div>
                    ) : null}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 10,
                        marginTop: 12,
                      }}
                    >
                      <div
                        style={{
                          padding: 12,
                          borderRadius: 16,
                          background: "#f8fafc",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
                          {t?.("market.catalog.main_calc", "Calculo principal") || "Calculo principal"}
                        </div>
                        <div style={{ marginTop: 6, fontWeight: 800 }}>
                          {t?.("market.catalog.current_price_short", "Atual: {{price}}", {
                            price: formatCurrency(entry.price),
                          }) || `Atual: ${formatCurrency(entry.price)}`}
                        </div>
                        <div style={{ marginTop: 6, color: "#64748b" }}>
                          {t?.("market.catalog.average_last_four", "Media 4 ultimos: {{price}}", {
                            price:
                              entryMetrics.averagePrice !== null
                                ? formatCurrency(entryMetrics.averagePrice)
                                : t?.("common.no_data", "n/d") || "n/d",
                          }) ||
                            `Media 4 ultimos: ${
                              entryMetrics.averagePrice !== null
                                ? formatCurrency(entryMetrics.averagePrice)
                                : "n/d"
                            }`}
                        </div>
                      </div>
                      <div
                        style={{
                          padding: 12,
                          borderRadius: 16,
                          background: "#f8fafc",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
                          {t?.("market.catalog.inflation_last_four", "Inflacao 4 ultimos") ||
                            "Inflacao 4 ultimos"}
                        </div>
                        <div style={{ marginTop: 6 }}>
                          <span style={getPermissionBadgeStyle(entryMetrics.inflationTone)}>
                            {entryMetrics.inflationLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredMarketPrices.length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 18,
                    border: "1px dashed #cbd5e1",
                    background: "#f8fafc",
                    color: "#64748b",
                  }}
                >
                  {t?.(
                    "market.catalog.empty_search",
                    "Nenhum produto encontrado para esta busca."
                  ) || "Nenhum produto encontrado para esta busca."}
                </div>
              ) : null}
              {!normalizedCatalogQuery && selectedMarketPrices.length > visibleMarketPrices.length ? (
                <div
                  style={{
                    padding: 14,
                    borderRadius: 18,
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    color: "#64748b",
                  }}
                >
                  {t?.(
                    "market.catalog.search_tip",
                    "Digite ao menos 2 letras para procurar direto no catalogo."
                  ) || "Digite ao menos 2 letras para procurar direto no catalogo."}
                </div>
              ) : null}
            </div>
          ) : (
            <div
              style={{
                padding: 18,
                borderRadius: 20,
                border: "1px solid #fed7aa",
                background: "#fff7ed",
                color: "#9a3412",
              }}
            >
              {t?.("market.catalog.empty", "Esse mercado ainda nao tem produtos disponiveis no catalogo sincronizado.") || "Esse mercado ainda nao tem produtos disponiveis no catalogo sincronizado."}
            </div>
          )}
        </div>

        <div style={cardStyle}>
          <SectionTitle
            eyebrow={t?.("market.selected.eyebrow", "Produto selecionado") || "Produto selecionado"}
            title={
              selectedMarketProduct
                ? selectedMarketProduct.product
                : t?.("market.selected.empty_title", "Escolha um produto") || "Escolha um produto"
            }
            description={
              selectedMarketProduct
                ? t?.(
                    "market.selected.description.active",
                    "Veja o preco atual, a promocao ativa e os 4 ultimos precos desse item neste mercado."
                  ) ||
                  "Veja o preco atual, a promocao ativa e os 4 ultimos precos desse item neste mercado."
                : t?.(
                    "market.selected.description.empty",
                    "Toque em um produto do catalogo para abrir o detalhe e comparar inflacao."
                  ) || "Toque em um produto do catalogo para abrir o detalhe e comparar inflacao."
            }
          />
          {selectedMarketProduct ? (
            <div style={{ display: "grid", gap: 16 }}>
              <div
                style={{
                  padding: 18,
                  borderRadius: 22,
                  background: "#fff7ed",
                  border: "1px solid #fdba74",
                }}
              >
                <div style={{ color: "#9a3412", fontSize: 13, fontWeight: 700 }}>
                  {t?.("market.selected.current_price", "Preco atual") || "Preco atual"}
                </div>
                <div style={{ marginTop: 8, fontSize: 30, fontWeight: 900, color: "#dc2626" }}>
                  {formatCurrency(selectedMarketProduct.price)}
                </div>
                <div style={{ marginTop: 10, color: "#475569" }}>
                  {t?.("market.selected.promotion", "Promocao: {{offer}}", {
                    offer:
                      selectedMarketProduct.offer_description ||
                      (t?.(
                        "market.selected.no_promotion",
                        "Sem promocao informada no momento"
                      ) || "Sem promocao informada no momento"),
                  }) || ""}
                </div>
                <div style={{ marginTop: 8, color: "#64748b" }}>
                  {t?.("market.catalog.updated_at", "Atualizado em {{date}}", {
                    date: formatCapturedAt(selectedMarketProduct.captured_at),
                  }) || `Atualizado em ${formatCapturedAt(selectedMarketProduct.captured_at)}`}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                  gap: 12,
                }}
              >
                <InsightCard
                  label={t?.("market.selected.history_label", "Historico analisado") || "Historico analisado"}
                  value={
                    t?.("market.selected.history_count", "{{count}} registros", {
                      count: selectedProductHistory.length,
                    }) || `${selectedProductHistory.length} registros`
                  }
                  helper={
                    t?.(
                      "market.selected.history_helper",
                      "O app mostra os 4 ultimos para comparacao rapida."
                    ) || "O app mostra os 4 ultimos para comparacao rapida."
                  }
                />
                <InsightCard
                  label={t?.("market.selected.local_inflation", "Inflacao local") || "Inflacao local"}
                  value={selectedProductMetrics.inflationLabel}
                  helper={
                    selectedProductMetrics.inflationPercentage === null
                      ? t?.(
                          "market.selected.local_inflation_empty",
                          "Adicione mais atualizacoes deste produto para acompanhar tendencia."
                        ) || "Adicione mais atualizacoes deste produto para acompanhar tendencia."
                      : t?.(
                          "market.selected.local_inflation_value",
                          "{{sign}}{{value}}% no recorte exibido",
                          {
                            sign: selectedProductMetrics.inflationPercentage > 0 ? "+" : "",
                            value: selectedProductMetrics.inflationPercentage.toFixed(1),
                          }
                        ) ||
                        `${selectedProductMetrics.inflationPercentage > 0 ? "+" : ""}${selectedProductMetrics.inflationPercentage.toFixed(1)}% no recorte exibido`
                  }
                  tone={selectedProductMetrics.inflationTone}
                />
              </div>

              <PriceHistoryChart points={selectedProductHistory} t={t} />

              <div
                style={{
                  padding: 16,
                  borderRadius: 20,
                  border: "1px solid #e5e7eb",
                  background: "#ffffff",
                }}
              >
                <strong style={{ display: "block", marginBottom: 12 }}>
                  {t?.("market.selected.last_four_prices", "Ultimos 4 precos neste mercado") ||
                    "Ultimos 4 precos neste mercado"}
                </strong>
                <div style={{ display: "grid", gap: 10 }}>
                  {selectedProductHistory.map((historyPoint, index) => (
                    <div
                      key={historyPoint.price_id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        padding: 12,
                        borderRadius: 16,
                        background: index === selectedProductHistory.length - 1 ? "#fff1f2" : "#f8fafc",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700 }}>
                          {index === selectedProductHistory.length - 1
                            ? t?.("market.selected.current_label", "Atual") || "Atual"
                            : t?.("market.selected.readings_ago", "{{count}} leitura(s) atras", {
                                count: index + 1,
                              }) || `${index + 1} leitura(s) atras`}
                        </div>
                        <div style={{ marginTop: 6, color: "#475569" }}>
                          {formatCapturedAt(historyPoint.captured_at)}
                        </div>
                      </div>
                      <div style={{ fontWeight: 800, color: "#dc2626" }}>
                        {formatCurrency(historyPoint.price)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ color: "#64748b" }}>
              {t?.(
                "market.selected.panel_empty",
                "Assim que voce selecionar um produto do catalogo, o painel mostra preco atual, promocao e tendencia dos ultimos registros."
              ) ||
                "Assim que voce selecionar um produto do catalogo, o painel mostra preco atual, promocao e tendencia dos ultimos registros."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
